import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { PenLine, RotateCcw, Type } from 'lucide-react';
import { Button } from './Button';

export type SignatureMethod = 'typed' | 'drawn';

export function signatureDataFromTypedName(name: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 900;
  canvas.height = 240;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Signature canvas is unavailable.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#111111';
  ctx.font = 'italic 72px cursive';
  ctx.textBaseline = 'middle';
  ctx.fillText(name.trim(), 40, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

export function SignatureCapture({ name, onNameChange, onReady }: {
  name: string;
  onNameChange: (value: string) => void;
  onReady: (payload: { method: SignatureMethod; data: string; ready: boolean }) => void;
}) {
  const [method, setMethod] = useState<SignatureMethod>('typed');
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  function resetCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 900;
    canvas.height = 220;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111111';
    setHasInk(false);
  }

  useEffect(() => { if (method === 'drawn') resetCanvas(); }, [method]);
  useEffect(() => {
    if (method === 'typed') {
      const ready = name.trim().length >= 2;
      onReady({ method, data: ready ? signatureDataFromTypedName(name) : '', ready });
      return;
    }
    const canvas = canvasRef.current;
    onReady({ method, data: hasInk && canvas ? canvas.toDataURL('image/png') : '', ready: hasInk });
  }, [method, name, hasInk, onReady]);

  function pointerPosition(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height
    };
  }

  function start(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (method !== 'drawn') return;
    const ctx = event.currentTarget.getContext('2d');
    if (!ctx) return;
    const p = pointerPosition(event);
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }

  function move(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || method !== 'drawn') return;
    const ctx = event.currentTarget.getContext('2d');
    if (!ctx) return;
    const p = pointerPosition(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  }

  function end(event: ReactPointerEvent<HTMLCanvasElement>) {
    drawing.current = false;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch {}
  }

  return <div className="signature-capture">
    <div className="signature-capture__tabs" role="tablist" aria-label="Signature method">
      <button type="button" className={method === 'typed' ? 'is-active' : ''} onClick={() => setMethod('typed')}><Type size={15} /> Type</button>
      <button type="button" className={method === 'drawn' ? 'is-active' : ''} onClick={() => setMethod('drawn')}><PenLine size={15} /> Draw</button>
    </div>
    {method === 'typed' ? <div className="signature-capture__typed">
      <label><span>Legal signature name</span><input value={name} onChange={(event) => onNameChange(event.target.value)} placeholder="Type full legal name" /></label>
      <div className="signature-capture__preview">{name.trim() || 'Your signature'}</div>
    </div> : <div className="signature-capture__drawn">
      <canvas ref={canvasRef} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} aria-label="Draw signature" />
      <Button tone="quiet" size="sm" onClick={resetCanvas} icon={<RotateCcw size={14} />}>Clear drawing</Button>
    </div>}
  </div>;
}
